/**
 * Innovation Readiness Assessment — v2.2
 * The IMPACT System™
 *
 * Changes from v1:
 * - 24 items → 34 items across 6 areas
 * - Scoring: area percentages (not raw totals)
 * - Screener moved to AFTER results (eliminates priming effect)
 * - Reflective prompt screen between areas (unscored)
 * - 6 reversed items: Q4, Q7, Q11, Q15, Q19, Q23
 * - Band thresholds: percentage-based
 * - Report API: passes districtSize, districtSetting, initiativeType
 */

import { useState, useEffect, useCallback, useRef } from "react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// ── BRAND ─────────────────────────────────────────────────────────────────────
const B = {
  forest: "#1C3D2E", forestMid: "#2A5940", amber: "#C47B10",
  amberLt: "#F5C84A", cream: "#F8F3EB", ink: "#14120E",
  stone: "#5C5248", rule: "#D4CABC", warmWhite: "#FDFBF8",
};
const T = {
  primary: "#FDFBF8", secondary: "rgba(253,251,248,0.85)",
  muted: "rgba(253,251,248,0.55)", tertiary: "rgba(253,251,248,0.65)",
  disabled: "rgba(253,251,248,0.35)",
};

// ── AREAS ──────────────────────────────────────────────────────────────────────
const AREAS = [
  {
    id: "clarity", num: 1, label: "Clarity of Initiative Intent", short: "Clarity",
    phase: "Identify", color: "#60A5FA", itemCount: 4,
    description: "How precisely defined is what your team is building.",
    reflectivePrompt: "Where is your team's clarity strongest? Where is the thinking still genuinely unfinished — and what would it take to finish it before you launch?",
  },
  {
    id: "stakeholder", num: 2, label: "Stakeholder Intelligence", short: "Stakeholders",
    phase: "Map", color: "#34D399", itemCount: 6,
    description: "How well you understand the people who will determine adoption.",
    reflectivePrompt: "Have you named every person whose position on this initiative you don't yet fully understand? Who is most likely to surprise you — and what would you do if they did?",
  },
  {
    id: "context", num: 3, label: "Contextual Awareness", short: "Context",
    phase: "Map", color: "#A78BFA", itemCount: 6,
    description: "How well you understand the forces shaping your initiative's environment.",
    reflectivePrompt: "What is the most significant force working against this initiative that you have not yet named publicly? And what existing community strength are you most at risk of overlooking?",
  },
  {
    id: "planning", num: 4, label: "Planning Architecture", short: "Planning",
    phase: "Plan", color: "#F59E0B", itemCount: 6,
    description: "Whether your plan can guide decisions without you in the room.",
    reflectivePrompt: "Could someone else run this initiative next week — without you — without losing the intent? And do the people responsible for day-to-day implementation have everything they need to succeed without coming back to you for direction?",
  },
  {
    id: "activation", num: 5, label: "Activation & Momentum", short: "Activation",
    phase: "Activate", color: "#F87171", itemCount: 7,
    description: "How deliberately you have designed the full launch arc.",
    reflectivePrompt: "What happens at month four — when the initial energy is gone and the hard work of changing what people do every day begins? Is that moment specifically designed for, or is your plan currently ending at launch day?",
  },
  {
    id: "learning", num: 6, label: "Learning & Adaptation", short: "Learning",
    phase: "Calibrate", color: "#2DD4BF", itemCount: 5,
    description: "Whether you have built a system for real-time course-correction.",
    reflectivePrompt: "Where in your organization would problems with this initiative be most likely to go unreported — and what does your honest answer tell you about what needs to change before you launch?",
  },
];

// ── QUESTIONS (34 items) ───────────────────────────────────────────────────────
// reversed: items that are reverse-scored (Score = 6 - raw)
// areaId: which area this belongs to
const QUESTIONS = [
  // ── Area 1: Clarity (4 items) ───────────────────────────────────────────────
  {
    id: 1, areaId: "clarity", reversed: false,
    text: "How would you describe the level of agreement among your leadership team about what this initiative is specifically trying to accomplish?",
    options: [
      "There is no shared understanding — team members have meaningfully different views of what we are doing",
      "There is rough agreement on general direction but significant disagreement on specifics",
      "There is moderate agreement but a few key members describe the initiative differently than others",
      "There is strong agreement on the initiative with only minor differences in how people describe the details",
      "Every member of the leadership team would describe this initiative the same way — same population, same outcome, same reason",
    ],
  },
  {
    id: 2, areaId: "clarity", reversed: false,
    text: "When asked 'what problem are you solving and for whom?', your team responds with:",
    options: [
      "A general answer about wanting to improve outcomes",
      "A broad problem area but multiple competing ideas about the approach",
      "A specific problem but the team is still debating the approach",
      "A specific problem, a named population, and a clear leading approach",
      "A concise, agreed-upon statement the full team can deliver immediately and consistently",
    ],
  },
  {
    id: 3, areaId: "clarity", reversed: false,
    text: "How clearly defined are the success criteria for this initiative?",
    options: [
      "Success would be difficult to define right now",
      "We have a general sense of what success looks like but no specific indicators",
      "We have some measures in mind but the team has not agreed on them",
      "We have 3–5 specific indicators the team has agreed on",
      "We have baseline data and specific, measurable targets for each indicator",
    ],
  },
  {
    id: 4, areaId: "clarity", reversed: true,
    text: "Our initiative scope expands as we learn more.",
    options: [
      "Very often — the scope has expanded substantially since we began and we struggle to contain it",
      "Often — the scope has shifted somewhat, usually in response to stakeholder or community input",
      "Sometimes — scope has changed but we have actively worked to manage it",
      "Rarely — we have a defined boundary and revisit it deliberately when needed",
      "Never — our scope is clearly bounded and has remained stable",
    ],
  },

  // ── Area 2: Stakeholder Intelligence (6 items) ──────────────────────────────
  {
    id: 5, areaId: "stakeholder", reversed: false,
    text: "When planning this initiative, how do you approach stakeholder analysis?",
    options: [
      "We identify who needs to formally approve the initiative",
      "We think about who supports versus who opposes it",
      "We map formal roles and make general assumptions about each person's likely position",
      "We analyze behavioral patterns and informal influence for key individuals",
      "We have built a written stakeholder map with a named engagement strategy for each key person",
    ],
  },
  {
    id: 6, areaId: "stakeholder", reversed: false,
    text: "How well do you understand the informal influence dynamics in your organization — the people who shape what others think without holding formal authority?",
    options: [
      "We rely primarily on the organizational chart to understand influence",
      "We recognize some informal influencers but have not mapped them systematically",
      "We have a general sense of who holds informal power in the organization",
      "We can identify the key informal influencers and anticipate how they are likely to respond",
      "We actively maintain a map of informal influence networks and update it as conditions change",
    ],
  },
  {
    id: 7, areaId: "stakeholder", reversed: true,
    text: "We engage stakeholders beyond those formally required to approve this initiative.",
    options: [
      "Never — our stakeholder engagement is limited to those whose formal approval is required",
      "Rarely — we occasionally reach beyond formal channels but it is not consistent practice",
      "Sometimes — we engage beyond formal approvers when we anticipate significant resistance",
      "Often — we routinely engage informal influencers as part of our stakeholder process",
      "Consistently — engaging beyond formal approvers is a deliberate and documented part of our approach",
    ],
  },
  {
    id: 8, areaId: "stakeholder", reversed: false,
    text: "How specifically have you mapped the positions of your board of education and, if applicable, union leadership on this initiative?",
    options: [
      "We have not specifically analyzed board or union positions on this initiative",
      "We have a general sense of where board members and union leadership stand but have not mapped it",
      "We have mapped the positions of most board members but have not developed engagement strategies",
      "We have mapped board and union positions and have a general engagement approach for each",
      "We have a specific, written engagement strategy for each board member and union leadership based on their known positions",
    ],
  },
  {
    id: 9, areaId: "stakeholder", reversed: false,
    text: "How directly have the people most affected by this initiative been involved in designing it?",
    options: [
      "The initiative was designed by the leadership team without meaningful input from those most affected",
      "We had substantive conversations with affected community members that changed our understanding of the problem — even if the formal design remained similar",
      "We gathered meaningful input from affected groups and it influenced some specific aspects of the design",
      "Affected community members, students, or families had a real voice in shaping the initiative's goals and approach, and their input produced visible changes",
      "The initiative's core goals and design were developed in genuine partnership with affected community members",
    ],
  },
  {
    id: 10, areaId: "stakeholder", reversed: false,
    text: "How directly have the teachers or staff responsible for implementing this initiative been involved in designing it?",
    options: [
      "Teachers or frontline staff were not involved in the design of this initiative",
      "We shared information with teachers and gathered some feedback but the design was not materially changed as a result",
      "We gathered meaningful input from teachers about what the initiative asks them to do and whether it is feasible within their current instructional context, and we made adjustments",
      "Teachers had a genuine role in shaping what the initiative asks them to do and how it will be supported",
      "The initiative was designed with substantial teacher input — the people responsible for implementation helped determine its goals, approach, and support structure",
    ],
  },

  // ── Area 3: Contextual Awareness (6 items) ──────────────────────────────────
  {
    id: 11, areaId: "context", reversed: false,
    text: "How actively does your team monitor forces that could accelerate or inhibit this initiative?",
    options: [
      "We address obstacles as they arise — we are not actively scanning in advance",
      "We identify obvious barriers before starting but do not monitor them continuously",
      "We scan for risks at the beginning of the initiative but our monitoring is not ongoing",
      "We regularly review the forces that could impact our work throughout implementation",
      "We have a systematic process for identifying accelerators, inhibitors, and early warning signals at every phase",
    ],
  },
  {
    id: 12, areaId: "context", reversed: false,
    text: "How specifically have you identified the knowledge, relationships, and capabilities that already exist in the community this initiative serves — and how are those strengths built into the design?",
    options: [
      "We have not specifically identified community assets relevant to this initiative",
      "We have a general sense that community strengths exist but have not mapped them or connected them to the initiative design",
      "We have identified some community assets and named them in planning conversations but have not integrated them into the design",
      "We have specifically identified community knowledge, relationships, or capabilities that are incorporated into how this initiative will work",
      "Community assets — named individuals, organizations, existing relationships, and local knowledge — are explicitly integrated into the initiative design",
    ],
  },
  {
    id: 13, areaId: "context", reversed: false,
    text: "How well does this initiative connect to your district's current strategic priorities?",
    options: [
      "The connection between this initiative and our strategic priorities is loose or unclear",
      "It relates to our priorities but is not explicitly tied to them in planning documents",
      "We have aligned the language but have not built structural connections",
      "This initiative is explicitly tied to strategic priorities and leadership is aware of the connection",
      "This initiative is embedded in our strategic plan with resource commitments attached",
    ],
  },
  {
    id: 14, areaId: "context", reversed: true,
    text: "External factors — such as policy changes, budget cycles, or personnel shifts — have caught us off guard during initiative work.",
    options: [
      "Never — we anticipate and actively plan for external disruptions before they occur",
      "Rarely — we occasionally miss signals but recover without significant damage",
      "Sometimes — external factors surprise us more than we would like",
      "Often — external disruptions frequently affect our initiative work in significant ways",
      "Very often — we are regularly caught off guard by external factors we did not anticipate",
    ],
  },
  {
    id: 15, areaId: "context", reversed: false,
    text: "How well do you understand the political landscape surrounding this initiative — including who supports it, who opposes it, and what interests drive each position?",
    options: [
      "We have not analyzed the political dimensions of this initiative",
      "We are aware of some political tensions but have not mapped them",
      "We understand the major political dynamics but have not developed specific mitigation strategies",
      "We have a clear picture of political risks and a general approach to managing them",
      "We have actively built coalitions of support and have specific strategies for each source of potential opposition",
    ],
  },
  {
    id: 16, areaId: "context", reversed: false,
    text: "How specifically have you identified and planned to monitor the equity implications of this initiative — who benefits, who is most affected, and whether outcomes will be equitable across student groups?",
    options: [
      "The equity implications of this initiative for specific student groups have not been explicitly identified or discussed",
      "We have had informal discussions about equity implications but have not established specific equity indicators or a formal equity review process",
      "We have identified which student groups are most affected but have not established specific equity indicators",
      "We have a clear equity frame with named student populations and specific indicators we will monitor for each group",
      "We have a documented equity theory of action — specific populations, measurable equity indicators, and a defined response plan for when differential outcomes appear",
    ],
  },

  // ── Area 4: Planning Architecture (6 items) ─────────────────────────────────
  {
    id: 17, areaId: "planning", reversed: false,
    text: "How would you describe your team's planning approach for this initiative?",
    options: [
      "We move quickly from idea to action with minimal formal planning",
      "We create basic plans but they are often incomplete or not consistently used",
      "We have structured plans but they are not consistently connected to daily decisions",
      "We use systematic planning that includes resources, stakeholders, and milestones",
      "Our plans are living documents that connect strategy to daily actions with clear accountability at every level",
    ],
  },
  {
    id: 18, areaId: "planning", reversed: false,
    text: "How well do your plans for this initiative account for the human side of change — including resistance, adoption, and capacity building?",
    options: [
      "Our plans focus on tasks and timelines — people dynamics are not addressed",
      "We note who is responsible for what but do not plan for resistance or adoption",
      "We consider some human factors but our planning is not systematic about this",
      "We explicitly plan for stakeholder engagement, likely resistance, and capacity building needs",
      "Our plans address how we will build awareness, desire, knowledge, and skill at every phase — before and after launch",
    ],
  },
  {
    id: 19, areaId: "planning", reversed: true,
    text: "We spend significant time planning this initiative before testing core assumptions with real stakeholders or students.",
    options: [
      "Never — we test core assumptions early and let the results shape our planning",
      "Rarely — we mostly plan based on evidence gathered from the field",
      "Sometimes — we occasionally over-plan before validating key assumptions",
      "Often — we tend to develop detailed plans before running meaningful tests with stakeholders",
      "Very often — extensive planning precedes any real-world testing of assumptions",
    ],
  },
  {
    id: 20, areaId: "planning", reversed: false,
    text: "How clearly defined are roles and accountability structures in your initiative work?",
    options: [
      "Roles are informal and often unclear to team members",
      "Roles are generally understood but have not been documented",
      "Roles are assigned and documented but accountability is inconsistently enforced",
      "Roles are clear, documented, and tied to specific outcomes with explicit accountability",
      "Every person involved in this initiative can name their role, their decision authority, and who they escalate to — and this is documented in a shared reference",
    ],
  },
  {
    id: 21, areaId: "planning", reversed: false,
    text: "How specifically have you identified and structured an implementation team — people with designated time and responsibility for supporting the day-to-day execution of this initiative?",
    options: [
      "We have not identified a specific implementation team — day-to-day execution is assumed to happen within existing roles",
      "We have a general sense of who will support implementation but roles have not been formally assigned",
      "We have identified individuals who will support implementation but they have not been given protected time or explicit accountability",
      "We have a named implementation team with defined roles and at least some protected time for implementation support",
      "We have a structured implementation team with explicit roles, protected time, clear decision authority, and regular meeting structures dedicated to implementation support",
    ],
  },
  {
    id: 22, areaId: "planning", reversed: false,
    text: "How specifically have you designed the support infrastructure that will help the people implementing this initiative build the skills they need — beyond initial training?",
    options: [
      "We have planned initial training and expect practitioners to develop the skills they need through experience and practice on the job",
      "We have planned some follow-up after training but it is not systematic or sustained",
      "We have a general plan for ongoing professional learning but coaching and practice structures are not yet defined",
      "We have designed a multi-phase skill-building plan that includes coaching, practice opportunities, and feedback loops beyond initial training",
      "We have an embedded, ongoing support structure — coaching, peer collaboration, and practice time — that is integrated into implementers' regular work for at least the first year",
    ],
  },

  // ── Area 5: Activation & Momentum (7 items) ─────────────────────────────────
  {
    id: 23, areaId: "activation", reversed: false,
    text: "How clearly have you communicated to your organization why this initiative is necessary now — not eventually, but now?",
    options: [
      "We have not yet made a clear case for urgency — the initiative has been announced but the 'why now' is unclear to most people",
      "We have shared some reasons but they are general — most staff do not feel a sense of urgency",
      "Leadership understands the urgency but it has not been communicated to the people who will implement this in terms that resonate with their work",
      "We have communicated a clear and specific case for why this initiative cannot wait — in terms that resonate with teachers and staff, not just leadership",
      "There is a shared understanding across the organization of the specific cost of not acting — and when asked, frontline implementers can articulate why this initiative matters now",
    ],
  },
  {
    id: 24, areaId: "activation", reversed: false,
    text: "How do you approach the launch of a new initiative?",
    options: [
      "We announce broadly and rely on the announcement itself to generate adoption",
      "We communicate the plan and provide basic training before or at launch",
      "We think about who we want to reach first but do not formally manage the launch sequence",
      "We identify early adopters and secure their visible, public commitment before broad announcement",
      "Before this initiative is announced broadly, a named group of credible early adopters is already visibly engaged and can speak to their experience",
    ],
  },
  {
    id: 25, areaId: "activation", reversed: false,
    text: "Before broad rollout, how specifically have you tested a component of this initiative with a real group of students, teachers, or staff?",
    options: [
      "We have not piloted any component of this initiative before broad rollout",
      "We have informally tried elements of this approach but have not treated it as a structured pilot",
      "We have piloted a component with a small group and gathered some feedback, though findings have not formally shaped the full design",
      "We have conducted a structured pilot with a defined group, gathered specific data, and used findings to refine the initiative before broad rollout",
      "We have a documented pilot with clear success criteria, a named group of participants, specific findings, and design changes made as a result — before broad announcement",
    ],
  },
  {
    id: 26, areaId: "activation", reversed: false,
    text: "How effectively does your team sustain momentum after the initial excitement fades — typically 3–6 months after launch?",
    options: [
      "Momentum typically fades within 3–6 months and we do not have a plan to address it",
      "We sustain some initiatives but struggle consistently when early energy fades",
      "We have check-in mechanisms in place but they are not consistently effective at renewing momentum",
      "We have regular reviews and actively address dips in momentum when we identify them",
      "We design for the post-launch momentum drop from the beginning — accountability structures, visible milestones, and recalibration triggers are planned before we launch",
    ],
  },
  {
    id: 27, areaId: "activation", reversed: true,
    text: "New initiatives at our institution compete with each other for attention and resources.",
    options: [
      "Never — we have clear processes for managing initiative volume and prioritization",
      "Rarely — competition occasionally appears but we address it before it damages our work",
      "Sometimes — initiative competition is known but only partially managed",
      "Often — too many initiatives compete for the same staff attention and organizational resources",
      "Very often — initiative overload is a chronic organizational problem that affects most of our work",
    ],
  },
  {
    id: 28, areaId: "activation", reversed: false,
    text: "How deliberately does your team build and communicate visible evidence of early progress to sustain commitment?",
    options: [
      "We do not actively manage evidence of early progress — it surfaces if it happens",
      "We document progress internally but do not communicate it strategically",
      "We share updates but do not specifically design for early wins",
      "We deliberately create early wins and communicate them to build credibility and momentum",
      "The first 90 days of this initiative are specifically designed to generate visible, shareable evidence of impact — before broader adoption is expected",
    ],
  },
  {
    id: 29, areaId: "activation", reversed: false,
    text: "How specifically have you planned how this initiative will be communicated to everyone it affects — including in accessible language and, where relevant, in multiple languages?",
    options: [
      "We have not yet developed a specific communication plan for this initiative",
      "We have a general plan to communicate broadly but have not tailored messages for different audiences",
      "We have different messages for different audiences but have not addressed language access or community-specific communication needs",
      "We have a multi-channel communication plan with tailored messages for key audiences and have considered language access needs",
      "We have a comprehensive communication plan — tailored by audience, tested with community representatives, available in all relevant languages, and designed to be updated as the initiative progresses",
    ],
  },

  // ── Area 6: Learning & Adaptation (5 items) ─────────────────────────────────
  {
    id: 30, areaId: "learning", reversed: false,
    text: "How does your team use data to adapt this initiative while it is in progress?",
    options: [
      "We collect data primarily for compliance reporting — it does not typically change what we do",
      "We review data at set intervals but rarely change course based on what we find",
      "We use data to make some adjustments but the process is informal and inconsistent",
      "We have regular data reviews that produce specific decisions about how to adapt the initiative",
      "We collect both fidelity data (are we implementing as designed?) and outcome data (is it working?), and each type informs different real-time decisions",
    ],
  },
  {
    id: 31, areaId: "learning", reversed: false,
    text: "How psychologically safe is it in your organization to report that this initiative is not working as planned?",
    options: [
      "Reporting problems is career-risky in our organization — we tend to report positively regardless of reality",
      "Some candor exists but there is clear pressure to demonstrate success",
      "Leadership accepts honest reporting but does not actively cultivate it or model it",
      "We explicitly model honest reporting of challenges and acknowledge it publicly when we change course",
      "We have built a culture where course corrections are treated as organizational intelligence — identifying problems early is celebrated, not penalized",
    ],
  },
  {
    id: 32, areaId: "learning", reversed: true,
    text: "In our organization, initiatives tend to be evaluated as either successful or failed — there is not much room for nuance.",
    options: [
      "Never — we consistently treat initiative work as a learning process with many gradations of progress",
      "Rarely — we mostly avoid binary success/failure framing and discuss outcomes with nuance",
      "Sometimes — binary thinking appears but leadership actively pushes back on it",
      "Often — success/failure framing is common in how our organization discusses initiative outcomes",
      "Very often — initiatives in our organization are publicly evaluated as successes or failures with little acknowledgment of complexity",
    ],
  },
  {
    id: 33, areaId: "learning", reversed: false,
    text: "How systematically does your organization capture and apply learning from past initiatives?",
    options: [
      "Learning from past initiatives is rarely documented and almost never formally applied to future work",
      "We debrief some initiatives but lessons do not consistently inform what we do next",
      "We document lessons learned but they do not reliably shape future planning",
      "We have a structured after-action process and its outputs feed directly into future initiative planning",
      "We maintain a living record of initiative insights — what worked, what didn't, and why — and it actively shapes every new initiative we begin",
    ],
  },
  {
    id: 34, areaId: "learning", reversed: false,
    text: "How specifically have you prepared for the range of community responses this initiative is likely to receive — given your district's track record with similar work?",
    options: [
      "We have not specifically considered how the community is likely to respond based on past experience with our district",
      "We have a general sense that community response may vary but have not developed a specific approach based on our district's history",
      "We have discussed how our district's track record may shape initial response and have identified the groups most likely to be skeptical",
      "We have a specific approach for each likely community response — including relationships and communication strategies prepared for skeptical communities",
      "We have mapped the likely response of each affected community based on their specific history with our district, and have named people and strategies ready to engage each one before launch",
    ],
  },
];

// ── SCREENER QUESTIONS (after instrument) ──────────────────────────────────────
const SCREENER = [
  {
    id: "districtSize",
    label: "District size",
    options: ["Under 1,000","1,000–3,000","3,001–7,500","7,501–15,000","15,001–30,000","Over 30,000"],
  },
  {
    id: "districtSetting",
    label: "District setting",
    options: ["Urban","Suburban","Rural","Mixed"],
  },
  {
    id: "initiativeType",
    label: "Type of initiative",
    options: ["Curriculum or instruction","Technology integration","School structure or redesign","Community or family partnership","Personnel or culture change","Other"],
  },
];

// ── SCORING ────────────────────────────────────────────────────────────────────
function scoreAnswers(answers) {
  const areaPcts = {};
  for (const area of AREAS) {
    const areaQs = QUESTIONS.filter(q => q.areaId === area.id);
    const scored = areaQs.map(q => {
      const raw = answers[q.id] ?? 3;
      return q.reversed ? 6 - raw : raw;
    });
    const sum = scored.reduce((a, b) => a + b, 0);
    areaPcts[area.id] = Math.round((sum / (areaQs.length * 5)) * 100);
  }
  const avg = Math.round(Object.values(areaPcts).reduce((a,b)=>a+b,0) / AREAS.length);
  const sorted = Object.entries(areaPcts).sort((a,b)=>b[1]-a[1]);
  const strongestId = sorted[0][0];
  const weakestId   = sorted[sorted.length-1][0];
  return { areaPcts, avg, strongestId, weakestId };
}

function getBand(avg) {
  if (avg < 45) return { label:"Foundation Stage", desc:"Significant gaps — substantial groundwork needed before launch.", color:"#EF4444" };
  if (avg < 65) return { label:"Developing Stage", desc:"Uneven capacity — some areas strong, others need targeted attention.", color:"#F97316" };
  if (avg < 80) return { label:"Emerging Stage",   desc:"Solid foundation — ready to deepen specific areas of practice.", color:"#3B82F6" };
  return            { label:"Advanced Stage",    desc:"Strong across all areas — ready for complex multi-initiative work.", color:"#22C55E" };
}

// ── UTILITIES ──────────────────────────────────────────────────────────────────
function isValidEmail(e) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e); }
const scr  = { display:"flex", alignItems:"flex-start", justifyContent:"center", minHeight:"100vh", padding:"32px 16px" };
const card = (w=520) => ({ background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.08)",
                            borderRadius:16, padding:"36px 32px", width:"100%", maxWidth:w, boxSizing:"border-box" });

// ── GLOBAL STYLES ──────────────────────────────────────────────────────────────
function GlobalStyles() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,400;0,500;1,400&family=Karla:wght@400;500;600;700;800&display=swap');
      *, *::before, *::after { box-sizing:border-box; }
      .ira-focus:focus-visible { outline:2px solid #F5C84A; outline-offset:3px; border-radius:6px; }
      .ira-opt:hover { background:rgba(255,255,255,0.07) !important; border-color:rgba(255,255,255,0.3) !important; }
      .ira-opt:focus-visible { outline:2px solid #F5C84A; outline-offset:2px; }
      @media(prefers-reduced-motion:reduce) { *{transition:none !important; animation:none !important;} }
    `}</style>
  );
}

function Logo({ size=18 }) {
  return (
    <div style={{ display:"inline-flex", alignItems:"center", gap:8 }}>
      <div style={{ width:size*1.6, height:size*1.6, background:B.amber, borderRadius:4,
                    display:"flex", alignItems:"center", justifyContent:"center" }}>
        <span style={{ color:B.ink, fontFamily:"'Lora',serif", fontWeight:500, fontSize:size*0.75, lineHeight:1 }}>I</span>
      </div>
      <span style={{ color:T.tertiary, fontFamily:"'Karla',sans-serif", fontWeight:800,
                     fontSize:size*0.72, letterSpacing:"0.15em", textTransform:"uppercase" }}>
        THE IMPACT SYSTEM™
      </span>
    </div>
  );
}

// ── MAIN APP ──────────────────────────────────────────────────────────────────
export default function Assessment() {
  useEffect(() => {
    document.title = "Innovation Readiness Assessment — The IMPACT System™";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', "Free 34-question diagnostic for superintendents. Find out where your district initiative is most at risk — before you launch. Results delivered to your inbox.");
  }, []);
  const [stage,      setStage]      = useState("welcome");
  const [firstName,  setFirstName]  = useState("");
  const [role,       setRole]       = useState("");
  const [answers,    setAnswers]    = useState({});
  const [currentQ,   setCurrentQ]   = useState(0);
  const [scores,     setScores]     = useState(null);
  const [email,      setEmail]      = useState("");
  const [screener,   setScreener]   = useState({});
  const [aiReport,   setAiReport]   = useState("");
  const [fading,     setFading]     = useState(false);
  const [liveMsg,    setLiveMsg]    = useState("");
  const [initiativeName,     setInitiativeName]     = useState("");
  const [implementationStage, setImplementationStage] = useState("");
  const [reflections,        setReflections]        = useState({});
  const [includeReflections, setIncludeReflections] = useState(false);
  const rm = window.matchMedia?.("(prefers-reduced-motion:reduce)").matches ?? false;

  const say = useCallback((m) => setLiveMsg(m), []);

  // Group questions by area for reflective prompt logic
  const q     = QUESTIONS[currentQ];
  const areaQs = q ? QUESTIONS.filter(qx => qx.areaId === q.areaId) : [];
  const isLastInArea = q ? QUESTIONS.indexOf(q) === QUESTIONS.findLastIndex(qx => qx.areaId === q.areaId) : false;
  const currentArea  = q ? AREAS.find(a => a.id === q.areaId) : null;

  // Total answered in current area
  const areaProgress = areaQs.filter(qx => answers[qx.id] !== undefined).length;
  const totalProgress = QUESTIONS.filter(qx => answers[qx.id] !== undefined).length;

  function selectAnswer(val) {
    const na = { ...answers, [q.id]: val };
    setAnswers(na);
    setTimeout(() => {
      setFading(true);
      setTimeout(() => {
        const isLast = currentQ === QUESTIONS.length - 1;
        if (isLast) {
          const s = scoreAnswers(na);
          setScores(s);
          setStage("results");
          say("Assessment complete. Viewing your results.");
          setFading(false);
        } else if (isLastInArea && currentArea) {
          // Show reflective prompt between areas
          setStage("reflect");
          say(`Area ${currentArea.num} complete. Reflecting.`);
          setFading(false);
        } else {
          setCurrentQ(c => c + 1);
          say(`Question ${currentQ + 2} of ${QUESTIONS.length}`);
          setFading(false);
        }
      }, rm ? 0 : 200);
    }, rm ? 40 : 400);
  }

  function continueFromReflect() {
    setCurrentQ(c => c + 1);
    setStage("question");
    say(`Question ${currentQ + 2} of ${QUESTIONS.length}`);
  }

  async function generateReport() {
    if (!isValidEmail(email)) return;
    setStage("loading");
    say("Generating your report. Please wait.");
    const band = getBand(scores.avg);
    const sortedAreas = [...AREAS].sort((a,b) => scores.areaPcts[b.id] - scores.areaPcts[a.id]);
    const reflectionsPayload = Object.fromEntries(
      Object.entries(reflections).filter(([_, v]) => v?.trim())
    );
    try {
      const res = await fetch(`${API_URL}/api/generate-report`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          role, firstName, email,
          totalScore: scores.avg,
          band,
          dimScores: scores.areaPcts,
          strongest: { id: scores.strongestId, label: AREAS.find(a=>a.id===scores.strongestId)?.label },
          weakest:   { id: scores.weakestId,   label: AREAS.find(a=>a.id===scores.weakestId)?.label },
          districtSize:       screener.districtSize    || "",
          districtSetting:    screener.districtSetting || "",
          initiativeType:     screener.initiativeType  || "",
          initiativeName:     initiativeName           || "",
          implementationStage: implementationStage     || "",
          reflections:        reflectionsPayload,
          includeReflections,
        }),
      });
      if (!res.ok) {
        const errText = await res.text();
        console.error("Report API error:", res.status, errText);
        throw new Error(`API ${res.status}: ${errText}`);
      }
      const data = await res.json();
      setAiReport(data.report || "");
      setStage("report");
      say("Your report is ready.");
    } catch (err) {
      console.error("Report generation failed:", err);
      setAiReport("Your report could not be generated at this time. Please contact matt@emberimpact.com directly.");
      setStage("report");
    }
  }

  return (
    <>
      <GlobalStyles />
      <div role="status" aria-live="polite" aria-atomic="true"
           style={{ position:"absolute", left:-10000, width:1, height:1, overflow:"hidden" }}>
        {liveMsg}
      </div>
      <div style={{
        minHeight:"100vh",
        backgroundColor: B.forest,
        backgroundImage: "linear-gradient(rgba(245,200,74,0.025) 1px,transparent 1px),linear-gradient(90deg,rgba(245,200,74,0.025) 1px,transparent 1px)",
        backgroundSize: "60px 60px",
        fontFamily: "'Karla','Helvetica Neue',sans-serif",
      }}>
        {stage === "welcome"  && <WelcomeScreen firstName={firstName} setFirstName={setFirstName} role={role} setRole={setRole}
                                   initiativeName={initiativeName} setInitiativeName={setInitiativeName}
                                   implementationStage={implementationStage} setImplementationStage={setImplementationStage}
                                   onStart={() => { setStage("question"); say("Assessment started. Question 1 of 34."); }} />}
        {stage === "question" && q && <QuestionScreen q={q} qIndex={currentQ} totalQ={QUESTIONS.length}
                                   answers={answers} fading={fading} rm={rm}
                                   currentArea={currentArea} areaProgress={areaProgress} areaTotal={areaQs.length}
                                   implementationStage={implementationStage}
                                   onSelect={selectAnswer}
                                   onBack={() => {
                                     if (currentQ > 0) {
                                       setFading(true);
                                       setTimeout(() => { setCurrentQ(c=>c-1); setFading(false);
                                         say(`Question ${currentQ} of ${QUESTIONS.length}`);
                                       }, rm ? 0 : 180);
                                     }
                                   }} />}
        {stage === "reflect"  && currentArea && <ReflectScreen area={currentArea}
                                   reflection={reflections[currentArea?.id] || ""}
                                   setReflection={(val) => setReflections(r => ({ ...r, [currentArea.id]: val }))}
                                   onContinue={continueFromReflect} />}
        {stage === "results"  && scores && <ResultsScreen scores={scores} firstName={firstName}
                                   email={email} setEmail={setEmail}
                                   screener={screener} setScreener={setScreener}
                                   reflections={reflections}
                                   includeReflections={includeReflections} setIncludeReflections={setIncludeReflections}
                                   onGenerate={generateReport} />}
        {stage === "loading"  && <LoadingScreen firstName={firstName} />}
        {stage === "report"   && scores && <ReportScreen scores={scores} aiReport={aiReport} firstName={firstName} email={email} />}
      </div>
    </>
  );
}

// ── WELCOME ────────────────────────────────────────────────────────────────────
function WelcomeScreen({ firstName, setFirstName, role, setRole, initiativeName, setInitiativeName, implementationStage, setImplementationStage, onStart }) {
  const roles = ["Superintendent","Assistant Superintendent","Principal","Curriculum Director",
                 "Director of Innovation","Higher Ed Leader","Other"];
  const canStart = role && firstName.trim().length > 0 && implementationStage;
  return (
    <main style={scr}>
      <div style={card(560)}>
        <div style={{ marginBottom:24, textAlign:"center" }}><Logo size={18} /></div>

        {/* Framing */}
        <div style={{ textAlign:"center", marginBottom:28 }}>
          <h1 style={{ fontFamily:"'Lora',Georgia,serif", fontSize:"2rem", fontWeight:400,
                       color:T.primary, lineHeight:1.2, marginBottom:12 }}>
            Before you launch.
          </h1>
          <p style={{ color:T.secondary, fontSize:"0.95rem", lineHeight:1.75, marginBottom:8 }}>
            The six areas in this diagnostic are the six things that determine whether a district initiative
            survives contact with your organization.
          </p>
          <p style={{ color:T.muted, fontSize:"0.87rem", lineHeight:1.7, marginBottom:0, fontStyle:"italic" }}>
            There is no right score. There is only honest thinking — before the initiative meets
            your organization instead of after.
          </p>
        </div>

        <div style={{ borderTop:"1px solid rgba(255,255,255,0.08)", paddingTop:24, marginBottom:20 }}>
          <p style={{ color:B.amberLt, fontSize:"0.75rem", letterSpacing:"0.14em",
                      textTransform:"uppercase", fontWeight:700, marginBottom:16, textAlign:"center" }}>
            34 questions · ~12 minutes · free diagnostic
          </p>

          {/* First name */}
          <div style={{ marginBottom:16 }}>
            <label htmlFor="first-name" style={{ display:"block", color:T.tertiary, fontSize:"0.72rem",
                                                 letterSpacing:"0.12em", textTransform:"uppercase", fontWeight:700, marginBottom:7 }}>
              First Name <span aria-label="required" style={{ color:"#FCA5A5" }}>*</span>
            </label>
            <input id="first-name" type="text" autoComplete="given-name" value={firstName}
              onChange={e => setFirstName(e.target.value)} placeholder="Your first name" aria-required="true"
              style={{ width:"100%", background:"rgba(255,255,255,0.07)", border:"1px solid rgba(255,255,255,0.15)",
                       borderRadius:8, color:T.primary, fontSize:"0.95rem", padding:"12px 14px",
                       outline:"none", fontFamily:"'Karla',sans-serif" }} />
          </div>

          {/* Role */}
          <div style={{ marginBottom:20 }}>
            <label style={{ display:"block", color:T.tertiary, fontSize:"0.72rem",
                            letterSpacing:"0.12em", textTransform:"uppercase", fontWeight:700, marginBottom:7 }}>
              Your Role <span aria-label="required" style={{ color:"#FCA5A5" }}>*</span>
            </label>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
              {roles.map(r => (
                <button key={r} onClick={() => setRole(r)} className="ira-focus"
                  style={{ background: role===r ? "rgba(196,123,16,0.2)" : "rgba(255,255,255,0.05)",
                           border: `1px solid ${role===r ? B.amber : "rgba(255,255,255,0.12)"}`,
                           borderRadius:8, color: role===r ? B.amberLt : T.secondary,
                           padding:"10px 12px", fontSize:"0.82rem", fontFamily:"'Karla',sans-serif",
                           fontWeight: role===r ? 700 : 500, cursor:"pointer", textAlign:"left",
                           transition:"all 0.15s" }}>
                  {r}
                </button>
              ))}
            </div>
          </div>

          {/* Initiative name (optional) */}
          <div style={{ marginBottom:20 }}>
            <label htmlFor="initiative-name" style={{ display:"block", color:T.tertiary, fontSize:"0.72rem",
                                                      letterSpacing:"0.12em", textTransform:"uppercase", fontWeight:700, marginBottom:7 }}>
              Initiative you're assessing
              <span style={{ color:T.muted, fontWeight:400, marginLeft:8 }}>(optional — one sentence)</span>
            </label>
            <input id="initiative-name" type="text" value={initiativeName}
              onChange={e => setInitiativeName(e.target.value)}
              placeholder="e.g. District-wide literacy framework for grades K–5"
              style={{ width:"100%", background:"rgba(255,255,255,0.07)", border:"1px solid rgba(255,255,255,0.15)",
                       borderRadius:8, color:T.primary, fontSize:"0.9rem", padding:"12px 14px",
                       outline:"none", fontFamily:"'Karla',sans-serif" }} />
          </div>

          {/* Implementation stage (required) */}
          <div style={{ marginBottom:24 }}>
            <label style={{ display:"block", color:T.tertiary, fontSize:"0.72rem",
                            letterSpacing:"0.12em", textTransform:"uppercase", fontWeight:700, marginBottom:10 }}>
              Stage of this initiative <span aria-label="required" style={{ color:"#FCA5A5" }}>*</span>
            </label>
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {[
                { value:"pre-launch", label:"Pre-launch", desc:"We haven't announced this yet" },
                { value:"early-launch", label:"Early launch", desc:"Announced, less than 3 months in" },
                { value:"mid-implementation", label:"Mid-implementation", desc:"3–12 months in" },
                { value:"relaunching", label:"Relaunching", desc:"Restarting or redesigning something that stalled" },
              ].map(opt => (
                <button key={opt.value} onClick={() => setImplementationStage(opt.value)} className="ira-focus"
                  style={{ display:"flex", justifyContent:"space-between", alignItems:"center",
                           padding:"11px 14px", borderRadius:8, cursor:"pointer", textAlign:"left",
                           background: implementationStage===opt.value ? "rgba(196,123,16,0.2)" : "rgba(255,255,255,0.05)",
                           border: `1px solid ${implementationStage===opt.value ? B.amber : "rgba(255,255,255,0.12)"}` }}>
                  <span style={{ color: implementationStage===opt.value ? B.amberLt : T.secondary,
                                 fontWeight: implementationStage===opt.value ? 700 : 500, fontSize:"0.87rem" }}>
                    {opt.label}
                  </span>
                  <span style={{ color:T.muted, fontSize:"0.8rem" }}>{opt.desc}</span>
                </button>
              ))}
            </div>
          </div>

          <button onClick={onStart} disabled={!canStart} className="ira-focus"
            style={{ width:"100%", padding:"14px", background: canStart ? B.amber : "rgba(255,255,255,0.1)",
                     color: canStart ? B.ink : T.disabled, border:"none", borderRadius:8,
                     fontSize:"0.95rem", fontWeight:800, fontFamily:"'Karla',sans-serif",
                     cursor: canStart ? "pointer" : "not-allowed", transition:"all 0.2s" }}>
            Begin the Assessment →
          </button>
        </div>
      </div>
    </main>
  );
}

// ── QUESTION ───────────────────────────────────────────────────────────────────
function QuestionScreen({ q, qIndex, totalQ, answers, fading, rm, currentArea, areaProgress, areaTotal, implementationStage, onSelect, onBack }) {
  const selected = answers[q.id];
  const overallPct = Math.round((qIndex / totalQ) * 100);
  const prelaunch = implementationStage === "pre-launch";
  const PRELAUNCH_NOTES = {
    21: "You haven't launched yet — respond based on the specificity of your current plan for building an implementation team.",
    22: "You haven't launched yet — respond based on the specificity of your current plan for ongoing skill-building support.",
    26: "You haven't launched this initiative — respond based on how you have sustained momentum in past initiatives, or your anticipated approach.",
    27: "If you currently have only one major initiative in progress, respond based on how your district has historically managed multiple simultaneous priorities.",
  };

  return (
    <main style={scr}>
      <div style={card(640)}>
        {/* Progress */}
        <div style={{ marginBottom:20 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
            <span style={{ color:T.tertiary, fontSize:"0.7rem", fontWeight:700, letterSpacing:"0.1em",
                           textTransform:"uppercase" }}>
              {currentArea?.short} · {areaProgress}/{areaTotal}
            </span>
            <span style={{ color:T.disabled, fontSize:"0.7rem" }}>{qIndex+1} of {totalQ}</span>
          </div>
          <div style={{ height:3, background:"rgba(255,255,255,0.08)", borderRadius:2 }}>
            <div style={{ height:"100%", borderRadius:2, background:currentArea?.color || B.amber,
                          width:`${overallPct}%`, transition:"width 0.3s ease" }} />
          </div>
        </div>

        {/* Question */}
        <div style={{ opacity: fading ? 0 : 1, transition: rm ? "none" : "opacity 0.18s ease" }}>
          <h2 style={{ fontFamily:"'Lora',Georgia,serif", fontSize:"1.2rem", fontWeight:400,
                       color:T.primary, lineHeight:1.5, marginBottom:20 }}>
            {q.text}
          </h2>

          {prelaunch && PRELAUNCH_NOTES[q.id] && (
            <div style={{ background:"rgba(196,123,16,0.08)", border:"1px solid rgba(196,123,16,0.2)",
                          borderRadius:8, padding:"10px 14px", marginBottom:16,
                          color:B.amberLt, fontSize:"0.8rem", lineHeight:1.6 }}>
              {PRELAUNCH_NOTES[q.id]}
            </div>
          )}

          {/* Options */}
          <div role="radiogroup" aria-label="Response options" style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {q.options.map((opt, i) => {
              const val = i + 1;
              const isSelected = selected === val;
              return (
                <button key={i} role="radio" aria-checked={isSelected} onClick={() => onSelect(val)}
                  className="ira-focus ira-opt"
                  style={{ display:"flex", alignItems:"flex-start", gap:12, padding:"12px 14px",
                           background: isSelected ? "rgba(196,123,16,0.15)" : "rgba(255,255,255,0.04)",
                           border: `1px solid ${isSelected ? B.amber : "rgba(255,255,255,0.1)"}`,
                           borderRadius:10, cursor:"pointer", textAlign:"left", transition:"all 0.12s",
                           width:"100%" }}>
                  <div style={{ width:22, height:22, minWidth:22, borderRadius:"50%",
                                border:`2px solid ${isSelected ? B.amber : "rgba(255,255,255,0.25)"}`,
                                background: isSelected ? B.amber : "transparent",
                                display:"flex", alignItems:"center", justifyContent:"center", marginTop:1 }}>
                    {isSelected && <div style={{ width:8, height:8, borderRadius:"50%", background:B.ink }} />}
                  </div>
                  <span style={{ color: isSelected ? T.primary : T.secondary, fontSize:"0.88rem",
                                 lineHeight:1.55, fontWeight: isSelected ? 600 : 400 }}>
                    {opt}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Back */}
        {qIndex > 0 && (
          <button onClick={onBack} className="ira-focus"
            style={{ background:"none", border:"none", color:T.muted, cursor:"pointer",
                     fontSize:"0.8rem", padding:"12px 0 0", fontFamily:"'Karla',sans-serif" }}>
            ← Back
          </button>
        )}
      </div>
    </main>
  );
}

// ── REFLECT ────────────────────────────────────────────────────────────────────
function ReflectScreen({ area, reflection, setReflection, onContinue }) {
  return (
    <main style={scr}>
      <div style={card(560)}>
        <div style={{ marginBottom:16 }}>
          <span style={{ color:area.color, fontFamily:"'Karla',sans-serif", fontSize:"0.72rem",
                         fontWeight:700, letterSpacing:"0.14em", textTransform:"uppercase" }}>
            Area {area.num} Complete · {area.label}
          </span>
        </div>
        <div style={{ borderLeft:`3px solid ${area.color}`, paddingLeft:16, marginBottom:20 }}>
          <p style={{ fontFamily:"'Lora',Georgia,serif", fontSize:"1.1rem", color:T.primary,
                      lineHeight:1.7, fontStyle:"italic", margin:0 }}>
            {area.reflectivePrompt}
          </p>
        </div>

        <div style={{ marginBottom:24 }}>
          <label htmlFor={`reflect-${area.id}`} style={{ display:"block", color:T.muted, fontSize:"0.72rem",
                    letterSpacing:"0.1em", textTransform:"uppercase", fontWeight:700, marginBottom:8 }}>
            What this area surfaces for you — optional
          </label>
          <textarea id={`reflect-${area.id}`} value={reflection || ""} onChange={e => setReflection(e.target.value)}
            rows={3} placeholder="Jot anything this surfaces for you..."
            style={{ width:"100%", background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.12)",
                     borderRadius:8, color:T.primary, fontSize:"0.88rem", padding:"12px 14px",
                     fontFamily:"'Karla',sans-serif", lineHeight:1.6, resize:"vertical", outline:"none", boxSizing:"border-box" }} />
          <p style={{ color:T.disabled, fontSize:"0.7rem", marginTop:6 }}>
            Not scored. Your notes are shared with Matt to help prepare for your conversation.
          </p>
        </div>

        <button onClick={onContinue} className="ira-focus"
          style={{ background:B.amber, color:B.ink, border:"none", borderRadius:8,
                   padding:"13px 28px", fontSize:"0.92rem", fontWeight:800,
                   fontFamily:"'Karla',sans-serif", cursor:"pointer" }}>
          Continue →
        </button>
      </div>
    </main>
  );
}

// ── RESULTS ────────────────────────────────────────────────────────────────────
function ResultsScreen({ scores, firstName, email, setEmail, screener, setScreener, reflections, includeReflections, setIncludeReflections, onGenerate }) {
  const band   = getBand(scores.avg);
  const sorted = [...AREAS].sort((a,b) => scores.areaPcts[b.id] - scores.areaPcts[a.id]);
  const emailValid = isValidEmail(email);

  return (
    <main style={{ ...scr, alignItems:"flex-start" }}>
      <div style={card(680)}>
        <div style={{ marginBottom:24 }}><Logo size={16} /></div>

        <header style={{ marginBottom:24 }}>
          <p style={{ color:T.tertiary, fontSize:"0.72rem", letterSpacing:"0.12em",
                      textTransform:"uppercase", fontWeight:700, marginBottom:8 }}>
            {firstName ? `${firstName}'s Readiness Profile` : "Your Readiness Profile"}
          </p>
          <h1 style={{ fontFamily:"'Lora',Georgia,serif", fontSize:"1.5rem", fontWeight:400,
                       color:T.primary, marginBottom:12, lineHeight:1.3 }}>
            Here's where your thinking is finished — and where it isn't.
          </h1>
          <span style={{ padding:"4px 14px", borderRadius:99, fontSize:"0.75rem", fontWeight:700,
                         border:`1px solid ${band.color}`, color:band.color }}>
            {band.label}
          </span>
        </header>

        {/* Area percentages */}
        <section aria-label="Your six readiness areas" style={{ marginBottom:28 }}>
          <p style={{ color:T.tertiary, fontSize:"0.68rem", letterSpacing:"0.14em",
                      textTransform:"uppercase", fontWeight:700, marginBottom:14 }}>
            Your Readiness Profile
          </p>
          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
            {sorted.map(area => {
              const pct = scores.areaPcts[area.id];
              return (
                <div key={area.id} aria-label={`${area.label}: ${pct}%`}
                  style={{ display:"flex", alignItems:"center", gap:12 }}>
                  <div style={{ width:90, color:T.secondary, fontSize:"0.75rem",
                                fontWeight:600, textAlign:"right", flexShrink:0 }}>
                    {area.short}
                  </div>
                  <div style={{ flex:1, height:8, background:"rgba(255,255,255,0.08)", borderRadius:4 }}>
                    <div style={{ height:"100%", borderRadius:4, background:area.color,
                                  width:`${pct}%`, transition:"width 0.6s ease" }} />
                  </div>
                  <div style={{ width:36, color:area.color, fontSize:"0.78rem",
                                fontWeight:700, textAlign:"right", flexShrink:0 }}>
                    {pct}%
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Screener */}
        <section style={{ marginBottom:24, background:"rgba(196,123,16,0.06)", border:"1px solid rgba(196,123,16,0.15)",
                          borderRadius:12, padding:"22px 20px" }}>
          <p style={{ color:B.amberLt, fontSize:"0.72rem", letterSpacing:"0.12em",
                      textTransform:"uppercase", fontWeight:700, marginBottom:4 }}>
            Almost done
          </p>
          <p style={{ color:T.secondary, fontSize:"0.85rem", lineHeight:1.55, marginBottom:18 }}>
            These three questions help us tailor your report to your district.
          </p>
          {SCREENER.map(sq => (
            <div key={sq.id} style={{ marginBottom:16 }}>
              <label style={{ display:"block", color:T.primary, fontSize:"0.78rem",
                              fontWeight:700, marginBottom:8 }}>
                {sq.label}
              </label>
              <div style={{ display:"flex", flexWrap:"wrap", gap:7 }}>
                {sq.options.map(opt => (
                  <button key={opt} onClick={() => setScreener(s => ({ ...s, [sq.id]:opt }))}
                    className="ira-focus"
                    style={{ padding:"8px 14px", borderRadius:99, fontSize:"0.78rem", fontWeight:600,
                             fontFamily:"'Karla',sans-serif", cursor:"pointer",
                             background: screener[sq.id]===opt ? "rgba(196,123,16,0.22)" : "rgba(255,255,255,0.06)",
                             border: `1.5px solid ${screener[sq.id]===opt ? B.amber : "rgba(255,255,255,0.15)"}`,
                             color: screener[sq.id]===opt ? B.amberLt : T.secondary,
                             transition:"all 0.12s" }}>
                    {opt}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </section>

        {/* Reflections toggle */}
        {Object.values(reflections).some(r => r?.trim()) && (
          <div style={{ marginBottom:20, padding:"16px", borderRadius:8,
                        background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.1)" }}>
            <label style={{ display:"flex", alignItems:"flex-start", gap:12, cursor:"pointer" }}>
              <input type="checkbox" checked={includeReflections} onChange={e => setIncludeReflections(e.target.checked)}
                style={{ marginTop:3, accentColor:B.amber, width:16, height:16 }} />
              <div>
                <div style={{ color:T.primary, fontSize:"0.88rem", fontWeight:600, marginBottom:4 }}>
                  Include my reflections in my emailed report
                </div>
                <div style={{ color:T.muted, fontSize:"0.78rem", lineHeight:1.55 }}>
                  Your notes will appear at the end of the report sent to your inbox.
                </div>
              </div>
            </label>
          </div>
        )}

        {/* Email capture */}
        <div style={{ borderTop:"1px solid rgba(255,255,255,0.08)", paddingTop:20 }}>
          <p style={{ color:T.secondary, fontSize:"0.88rem", lineHeight:1.65, marginBottom:16 }}>
            Enter your email to receive your full report and the Innovation Readiness Guide —
            delivered to your inbox.
          </p>
          <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="your@email.edu" aria-label="Email address" autoComplete="email"
              style={{ flex:1, minWidth:220, background:"rgba(255,255,255,0.07)",
                       border:"1px solid rgba(255,255,255,0.15)", borderRadius:8,
                       color:T.primary, fontSize:"0.9rem", padding:"12px 14px",
                       outline:"none", fontFamily:"'Karla',sans-serif" }} />
            <button onClick={onGenerate} disabled={!emailValid} className="ira-focus"
              style={{ padding:"12px 22px", background: emailValid ? B.amber : "rgba(255,255,255,0.08)",
                       color: emailValid ? B.ink : T.disabled, border:"none", borderRadius:8,
                       fontSize:"0.88rem", fontWeight:800, fontFamily:"'Karla',sans-serif",
                       cursor: emailValid ? "pointer" : "not-allowed", whiteSpace:"nowrap" }}>
              Send Me My Report →
            </button>
          </div>
        </div>
      </div>
    </main>
  );
}

// ── LOADING ────────────────────────────────────────────────────────────────────
function LoadingScreen({ firstName }) {
  return (
    <main style={{ ...scr, alignItems:"center" }}>
      <div style={{ ...card(480), textAlign:"center" }}>
        <Logo size={16} />
        <p style={{ color:T.secondary, marginTop:24, fontSize:"0.95rem", lineHeight:1.7 }}>
          {firstName ? `Preparing your report, ${firstName}…` : "Preparing your report…"}
        </p>
        <p style={{ color:T.muted, fontSize:"0.82rem" }}>This usually takes 15–20 seconds.</p>
      </div>
    </main>
  );
}

// ── GUIDE FORM (inline on report screen) ─────────────────────────────────────
function GuideFormSection({firstName, email}) {
  const [submitted, setSubmitted] = useState(false);
  const [name, setName] = useState(firstName||'');
  const [mail, setMail] = useState(email||'');
  const formRef = useRef(null);

  function handleSubmit(e) {
    e.preventDefault();
    if(!mail||!mail.includes('@')) return;
    let iframe = document.getElementById('kit-guide-iframe');
    if(!iframe){
      iframe = document.createElement('iframe');
      iframe.id = 'kit-guide-iframe';
      iframe.name = 'kit-guide-iframe';
      iframe.style.display = 'none';
      document.body.appendChild(iframe);
    }
    formRef.current.target = 'kit-guide-iframe';
    formRef.current.submit();
    setSubmitted(true);
  }

  if(submitted){
    return (
      <section style={{background:"rgba(61,122,88,0.12)",border:"1px solid rgba(61,122,88,0.25)",borderRadius:12,padding:"20px 20px",marginBottom:24,textAlign:"center"}}>
        <p style={{color:"#34D399",fontSize:"0.88rem",fontWeight:700,marginBottom:6}}>Check your inbox</p>
        <p style={{color:T.secondary,fontSize:"0.82rem",lineHeight:1.5}}>We sent a confirmation email. Click the link to download your guide.</p>
      </section>
    );
  }

  return (
    <section style={{background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:12,padding:"20px 20px 16px",marginBottom:24}}>
      <p style={{color:T.tertiary,fontSize:"0.68rem",letterSpacing:"0.15em",textTransform:"uppercase",fontWeight:700,marginBottom:6}}>Get the Innovation Readiness Guide</p>
      <p style={{color:T.secondary,fontSize:"0.82rem",lineHeight:1.5,marginBottom:14}}>Five questions every leader should answer before launching. Confirm your email and the guide downloads immediately.</p>
      <form ref={formRef} action="https://app.kit.com/forms/9281802/subscriptions" method="post" onSubmit={handleSubmit}>
        <div style={{marginBottom:10}}>
          <input name="fields[first_name]" type="text" placeholder="First name" value={name} onChange={e=>setName(e.target.value)} aria-label="First Name"
            style={{width:"100%",padding:"12px 16px",background:"rgba(255,255,255,0.06)",border:"1.5px solid rgba(255,255,255,0.15)",borderRadius:8,color:T.primary,fontSize:"0.9rem",fontFamily:"'Karla',sans-serif",outline:"none",boxSizing:"border-box"}} />
        </div>
        <div style={{marginBottom:12}}>
          <input name="email_address" type="email" required placeholder="you@district.org" value={mail} onChange={e=>setMail(e.target.value)} aria-label="Email Address"
            style={{width:"100%",padding:"12px 16px",background:"rgba(255,255,255,0.06)",border:"1.5px solid rgba(255,255,255,0.15)",borderRadius:8,color:T.primary,fontSize:"0.9rem",fontFamily:"'Karla',sans-serif",outline:"none",boxSizing:"border-box"}} />
        </div>
        <button type="submit"
          style={{display:"block",width:"100%",padding:"14px 24px",background:B.amber,color:B.ink,border:"none",borderRadius:8,fontFamily:"'Karla',sans-serif",fontSize:"0.88rem",fontWeight:800,letterSpacing:"0.03em",cursor:"pointer"}}>
          Get the Guide &rarr;
        </button>
      </form>
      <p style={{color:T.disabled,fontSize:"0.65rem",marginTop:8,textAlign:"center"}}>We'll send a confirmation email. Click the link to download your guide.</p>
    </section>
  );
}


// ── REPORT ─────────────────────────────────────────────────────────────────────
function ReportScreen({ scores, aiReport, firstName, email }) {
  const band   = getBand(scores.avg);
  const sorted = [...AREAS].sort((a,b) => scores.areaPcts[b.id] - scores.areaPcts[a.id]);
  const paras  = aiReport.split(/\n\n+/).filter(p => p.trim());
  return (
    <main style={{ ...scr, alignItems:"flex-start" }}>
      <div style={card(700)}>
        <div style={{ marginBottom:24 }}><Logo size={16} /></div>

        <header style={{ marginBottom:28 }}>
          <p style={{ color:T.tertiary, fontSize:"0.72rem", letterSpacing:"0.12em",
                      textTransform:"uppercase", fontWeight:700, marginBottom:8 }}>
            {firstName ? `${firstName}'s Analysis` : "Your Analysis"}
          </p>
          <h1 style={{ fontFamily:"'Lora',Georgia,serif", fontSize:"1.5rem", fontWeight:400,
                       color:T.primary, marginBottom:14, lineHeight:1.3 }}>
            Here's what your results show — and what to do about it.
          </h1>
          <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
            <span style={{ padding:"4px 12px", borderRadius:99, fontSize:"0.72rem", fontWeight:700,
                           border:`1px solid ${band.color}`, color:band.color }}>
              {band.label}
            </span>
            <span style={{ padding:"4px 12px", borderRadius:99, fontSize:"0.72rem", fontWeight:700,
                           border:"1px solid rgba(255,255,255,0.2)", color:T.muted }}>
              Strongest: {AREAS.find(a=>a.id===scores.strongestId)?.short}
            </span>
            <span style={{ padding:"4px 12px", borderRadius:99, fontSize:"0.72rem", fontWeight:700,
                           border:"1px solid rgba(255,255,255,0.2)", color:T.muted }}>
              Priority gap: {AREAS.find(a=>a.id===scores.weakestId)?.short}
            </span>
          </div>
        </header>

        {/* Report body */}
        <article aria-label="Your assessment report" style={{ marginBottom:28 }}>
          {paras.map((p, i) => (
            <p key={i} style={{ color: i===0 ? T.primary : T.secondary,
                                fontSize: i===0 ? "1rem" : "0.92rem",
                                lineHeight:1.8, marginBottom:16,
                                fontFamily:"'Karla',sans-serif" }}>
              {p}
            </p>
          ))}
        </article>

        {/* Donut charts */}
        <section aria-label="Your six readiness areas" style={{ marginBottom:28 }}>
          <p style={{ color:T.tertiary, fontSize:"0.68rem", letterSpacing:"0.14em",
                      textTransform:"uppercase", fontWeight:700, marginBottom:14 }}>
            Your Readiness Profile
          </p>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10 }}>
            {sorted.map(area => {
              const pct = scores.areaPcts[area.id];
              const size=80,radius=34,strokeW=7,circ=2*Math.PI*radius,filled=circ*(pct/100),gap=circ-filled;
              return (
                <div key={area.id} aria-label={`${area.label}: ${pct}%`}
                  style={{ textAlign:"center", padding:"18px 8px 14px", background:"rgba(255,255,255,0.03)",
                           borderRadius:8, border:`1px solid ${area.color}25` }}>
                  <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true"
                    style={{ display:"block", margin:"0 auto 10px", overflow:"visible" }}>
                    <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={strokeW}/>
                    <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke={area.color} strokeWidth={strokeW} strokeLinecap="round"
                      strokeDasharray={`${filled} ${gap}`} transform={`rotate(-90 ${size/2} ${size/2})`}/>
                    <text x={size/2} y={size/2-2} textAnchor="middle" dominantBaseline="central"
                      style={{ fontSize:"1.1rem", fontWeight:900, fill:area.color, fontFamily:"'Karla',sans-serif" }}>{pct}%</text>
                  </svg>
                  <div style={{ color:T.tertiary, fontSize:"0.62rem", fontWeight:700 }}>{area.num}. {area.short}</div>
                </div>
              );
            })}
          </div>
        </section>

        {/* CTA */}
        <footer style={{ background:"rgba(196,123,16,0.06)", border:"1px solid rgba(196,123,16,0.15)",
                         borderRadius:12, padding:"24px 20px", textAlign:"center", marginBottom:24 }}>
          <p style={{ color:T.primary, fontSize:"0.95rem", fontWeight:700, marginBottom:8,
                      fontFamily:"'Lora',Georgia,serif" }}>
            Want to talk through what this means?
          </p>
          <p style={{ color:T.secondary, fontSize:"0.84rem", marginBottom:20, lineHeight:1.65 }}>
            These gaps are worth addressing before you launch. The next step is a conversation —
            your results, your district, what this actually means for what you're trying to do.
          </p>
          <a href="https://calendly.com/matt-emberimpact/discovery-call-15-min" className="ira-focus"
            style={{ display:"inline-block", padding:"14px 26px", background:B.amber, color:B.ink,
                     borderRadius:8, textDecoration:"none", fontFamily:"'Karla',sans-serif",
                     fontWeight:800, fontSize:"0.92rem" }}>
            Let's talk about your results →
          </a>
          <p style={{ color:T.muted, fontSize:"0.7rem", marginTop:14, lineHeight:1.5 }}>
            Matthew Callison, PhD · Director of Innovation &amp; Strategic Partnerships<br/>
            South Fayette Township School District
          </p>
        </footer>

        {/* Get the Guide — Kit form */}
        <GuideFormSection firstName={firstName} email={email} />

        <p style={{ color:T.disabled, fontSize:"0.67rem", textAlign:"center", marginTop:20 }}>
          © 2026 Matthew Callison, PhD · The IMPACT System™ · All rights reserved
        </p>
      </div>
    </main>
  );
}
